import React, { useState, useEffect, useRef } from 'react';
import { Check, ArrowRight, ShieldCheck, CircleNotch, WarningCircle, CheckCircle, Eye, EyeSlash } from '@phosphor-icons/react';

const PurchasePage = () => {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if logged-in user (existing trial tenant)
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user.tenant_id || null;

  // Institute signup details collected BEFORE payment (prevents post-payment duplicate failures)
  const [signupForm, setSignupForm] = useState({
    institute_name: '',
    subdomain: '',
    college_code: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    phone: '',
  });

  // Billing details for GST-compliant invoice
  const [billingForm, setBillingForm] = useState({
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_pincode: '',
    gst_number: '',
  });

  // Availability status per field (green tick / red cross + reason)
  const [availability, setAvailability] = useState({
    subdomain: { checking: false, available: null, reason: null },
    college_code: { checking: false, available: null, reason: null },
    admin_email: { checking: false, available: null, reason: null },
  });

  const debounceRefs = useRef({ subdomain: null, college_code: null, admin_email: null });
  const trackTimerRef = useRef(null);

  useEffect(() => {
    fetchPlans();
  }, []); // eslint-disable-line

  // Recover abandoned cart from email link (?recover=<token>)
  useEffect(() => {
    if (tenantId) return; // existing tenants never recover a cart
    const params = new URLSearchParams(window.location.search);
    const token = params.get('recover');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/recover-cart/${token}`);
        if (!res.ok) return;
        const cart = await res.json();
        const signupKeys = ['institute_name', 'subdomain', 'college_code', 'admin_name', 'phone'];
        const billingKeys = ['billing_address', 'billing_city', 'billing_state', 'billing_pincode', 'gst_number'];
        setSignupForm(f => {
          const next = { ...f };
          signupKeys.forEach(k => { if (cart[k]) next[k] = cart[k]; });
          if (cart.email) next.admin_email = cart.email;
          return next;
        });
        setBillingForm(b => {
          const next = { ...b };
          billingKeys.forEach(k => { if (cart[k]) next[k] = cart[k]; });
          return next;
        });
        if (cart.billing_cycle) setBillingCycle(cart.billing_cycle);
        // Kick off availability checks for restored fields
        if (cart.subdomain) checkField('subdomain', cart.subdomain);
        if (cart.college_code) checkField('college_code', cart.college_code);
        if (cart.email) checkField('admin_email', cart.email);
      } catch {
        // Silent — recovery is a nice-to-have, never block the page
      }
    })();
  }, []); // eslint-disable-line

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/plans`);
      const data = await res.json();
      const plansList = data.plans || data || [];
      const purchasePlans = plansList.filter(p => ['basic', 'standard', 'premium'].includes(p.code || p.id));
      const order = { basic: 1, standard: 2, premium: 3 };
      purchasePlans.sort((a, b) => (order[a.code || a.id] || 99) - (order[b.code || b.id] || 99));
      setPlans(purchasePlans);
    } catch (e) {
      console.error('Failed to fetch plans:', e);
    } finally {
      setPageLoading(false);
    }
  };

  const getPlanPrice = (plan) => {
    if (billingCycle === 'yearly') return plan.amount_yearly || plan.price_yearly || 0;
    return plan.amount_monthly || plan.price_monthly || 0;
  };

  // Debounced availability check per field
  const checkField = (field, value) => {
    if (debounceRefs.current[field]) clearTimeout(debounceRefs.current[field]);
    if (!value || value.trim().length < 2) {
      setAvailability(a => ({ ...a, [field]: { checking: false, available: null, reason: null } }));
      return;
    }
    setAvailability(a => ({ ...a, [field]: { checking: true, available: null, reason: null } }));
    debounceRefs.current[field] = setTimeout(async () => {
      try {
        const body = {};
        if (field === 'admin_email') body.email = value;
        else body[field] = value;
        const res = await fetch(`${API_URL}/api/public/check-availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const key = field === 'admin_email' ? 'email' : field;
        const status = data[key] || {};
        setAvailability(a => ({ ...a, [field]: { checking: false, available: status.available, reason: status.reason } }));
      } catch {
        setAvailability(a => ({ ...a, [field]: { checking: false, available: null, reason: 'Could not verify' } }));
      }
    }, 500);
  };

  const handleSignupChange = (field, value) => {
    let clean = value;
    if (field === 'subdomain') clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (field === 'college_code') clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    if (field === 'admin_email') clean = value.trim();
    setSignupForm(f => ({ ...f, [field]: clean }));
    if (['subdomain', 'college_code', 'admin_email'].includes(field)) {
      checkField(field, clean);
    }
    // Trigger debounced cart tracking — only meaningful once we have an email
    scheduleCartTrack({ [field]: clean });
  };

  // Abandoned cart tracking — fires 2s after user pauses typing, only if email is present
  const scheduleCartTrack = (partial = {}) => {
    if (tenantId) return; // logged-in users never abandon a cart
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(async () => {
      const latest = { ...signupForm, ...partial };
      const email = (latest.admin_email || '').trim();
      // Require a valid-looking email before sending (cheap check, backend validates too)
      if (!email || !email.includes('@') || !email.includes('.')) return;
      try {
        await fetch(`${API_URL}/api/public/track-cart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            institute_name: latest.institute_name || null,
            subdomain: latest.subdomain || null,
            college_code: latest.college_code || null,
            admin_name: latest.admin_name || null,
            phone: latest.phone || null,
            billing_cycle: billingCycle,
            ...billingForm,
          }),
        });
      } catch {
        // Silent — abandoned cart tracking must never block the form
      }
    }, 2000);
  };

  const validateSignupForm = () => {
    if (tenantId) return null; // existing user, renewing
    const required = { institute_name: 'Institute Name', subdomain: 'Subdomain', college_code: 'College Code', admin_name: 'Admin Name', admin_email: 'Admin Email', admin_password: 'Password', phone: 'Phone' };
    for (const [k, label] of Object.entries(required)) {
      if (!signupForm[k] || !signupForm[k].toString().trim()) return `${label} is required`;
    }
    if (signupForm.admin_password.length < 6) return 'Password must be at least 6 characters';
    for (const f of ['subdomain', 'college_code', 'admin_email']) {
      if (availability[f].available === false) {
        return availability[f].reason || `${f} is not available`;
      }
      if (availability[f].available === null || availability[f].checking) {
        return 'Please wait while we verify your details...';
      }
    }
    return null;
  };

  const handlePurchase = async (plan) => {
    const planCode = plan.code || plan.id;
    setSelectedPlan(planCode);
    setError('');

    const validationError = validateSignupForm();
    if (validationError) {
      setError(validationError);
      setSelectedPlan(null);
      // Scroll to top so error is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    try {
      const orderBody = {
        plan: planCode,
        billing_cycle: billingCycle,
        tenant_id: tenantId,
        ...(tenantId ? {} : signupForm),
        ...billingForm,
      };
      const res = await fetch(`${API_URL}/api/public/create-purchase-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderBody),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create order');

      const options = {
        key: data.razorpay_key,
        amount: data.amount * 100,
        currency: data.currency,
        name: 'CloudCampus',
        description: `${data.plan_name} - ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'}`,
        order_id: data.order_id,
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/public/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: planCode,
                billing_cycle: billingCycle,
                tenant_id: tenantId,
              }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.status === 'success') {
              if (tenantId) {
                alert('Payment successful! Your subscription is now active.');
                const tenantCode = localStorage.getItem('tenant_code');
                window.location.href = tenantCode ? `/${tenantCode}/dashboard` : '/dashboard';
              } else {
                // Prefill StartTrial with all signup + billing details so user just clicks "Activate"
                const q = new URLSearchParams({
                  plan: planCode,
                  billing: billingCycle,
                  payment_id: response.razorpay_payment_id,
                  ...signupForm,
                  ...billingForm,
                }).toString();
                window.location.href = `/start-trial?${q}`;
              }
            }
          } catch {
            alert('Payment verified but activation pending. Please contact support.');
          }
        },
        prefill: {
          name: signupForm.admin_name || '',
          email: signupForm.admin_email || '',
          contact: signupForm.phone || '',
        },
        theme: { color: '#0a1628' },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        throw new Error('Payment gateway not loaded. Please refresh and try again.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const getPopularIndex = () => Math.floor(plans.length / 2);

  const FieldStatus = ({ field }) => {
    const s = availability[field];
    if (s.checking) return <span className="text-[11px] text-gray-400 flex items-center gap-1"><CircleNotch size={11} className="animate-spin" /> Checking…</span>;
    if (s.available === true) return <span className="text-[11px] text-green-600 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Available</span>;
    if (s.available === false) return <span className="text-[11px] text-red-600 flex items-center gap-1"><WarningCircle size={11} weight="fill" /> {s.reason}</span>;
    return null;
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0a1628]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] px-4 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/cloudcampus-logo.png" alt="CloudCampus" className="h-12" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-[#0a1628] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Choose Your Plan</h1>
          <p className="text-gray-500 max-w-md mx-auto">Select the perfect plan for your institute. All plans include 24/7 support and free data migration.</p>

          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition ${billingCycle === 'monthly' ? 'bg-[#0a1628] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'}`} data-testid="purchase-monthly">Monthly</button>
            <button onClick={() => setBillingCycle('yearly')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition ${billingCycle === 'yearly' ? 'bg-[#0a1628] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'}`} data-testid="purchase-yearly">
              Annually <span className="text-green-500 text-xs ml-1">Save More</span>
            </button>
          </div>
        </div>

        {error && <div className="max-w-3xl mx-auto mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 text-center" data-testid="purchase-error">{error}</div>}

        {/* Signup details — required BEFORE payment for new users */}
        {!tenantId && (
          <div className="max-w-3xl mx-auto mb-6 bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-[#0a1628] mb-1">Institute Details <span className="text-red-500">*</span></h3>
            <p className="text-xs text-gray-400 mb-4">We verify these before payment to ensure your account activates instantly after you pay.</p>
            <div className="space-y-3">
              <input type="text" value={signupForm.institute_name} onChange={e => handleSignupChange('institute_name', e.target.value)} placeholder="Institute Name" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-institute-name" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input type="text" value={signupForm.subdomain} onChange={e => handleSignupChange('subdomain', e.target.value)} placeholder="Subdomain (e.g. moderncollege)" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-subdomain" />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-[11px] text-gray-400">cloudcampus.site/{signupForm.subdomain || '…'}</p>
                    <FieldStatus field="subdomain" />
                  </div>
                </div>
                <div>
                  <input type="text" value={signupForm.college_code} onChange={e => handleSignupChange('college_code', e.target.value)} placeholder="College Code (e.g. MCE)" maxLength={10} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none uppercase font-mono" data-testid="purchase-college-code" />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-[11px] text-gray-400">Used in Student IDs</p>
                    <FieldStatus field="college_code" />
                  </div>
                </div>
              </div>

              <input type="text" value={signupForm.admin_name} onChange={e => handleSignupChange('admin_name', e.target.value)} placeholder="Admin Full Name" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-admin-name" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input type="email" value={signupForm.admin_email} onChange={e => handleSignupChange('admin_email', e.target.value)} placeholder="Admin Email" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-admin-email" />
                  <div className="mt-1 flex items-center justify-end">
                    <FieldStatus field="admin_email" />
                  </div>
                </div>
                <input type="tel" value={signupForm.phone} onChange={e => handleSignupChange('phone', e.target.value)} placeholder="+91 9876543210" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-phone" />
              </div>

              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} minLength={6} value={signupForm.admin_password} onChange={e => handleSignupChange('admin_password', e.target.value)} placeholder="Password (min 6 characters)" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none pr-10" data-testid="purchase-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-testid="purchase-password-toggle">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Billing Details — required for GST-compliant invoice */}
        <div className="max-w-3xl mx-auto mb-10 bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-[#0a1628] mb-3">Billing Details <span className="text-gray-400 text-xs font-normal">(for tax invoice)</span></h3>
          <div className="space-y-3">
            <input type="text" value={billingForm.billing_address} onChange={e => setBillingForm({ ...billingForm, billing_address: e.target.value })} placeholder="Billing Address" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-billing-address" />
            <div className="grid grid-cols-3 gap-3">
              <input type="text" value={billingForm.billing_city} onChange={e => setBillingForm({ ...billingForm, billing_city: e.target.value })} placeholder="City" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-billing-city" />
              <input type="text" value={billingForm.billing_state} onChange={e => setBillingForm({ ...billingForm, billing_state: e.target.value })} placeholder="State" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-billing-state" />
              <input type="text" maxLength={6} value={billingForm.billing_pincode} onChange={e => setBillingForm({ ...billingForm, billing_pincode: e.target.value.replace(/\D/g, '') })} placeholder="PIN code" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="purchase-billing-pincode" />
            </div>
            <input type="text" maxLength={15} value={billingForm.gst_number} onChange={e => setBillingForm({ ...billingForm, gst_number: e.target.value.toUpperCase() })} placeholder="GST Number (optional, for Input Tax Credit)" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none uppercase font-mono" data-testid="purchase-gst-number" />
            <p className="text-xs text-gray-400">Add your GSTIN to receive a tax invoice eligible for ITC claim.</p>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(plans.length, 4)} gap-5`}>
          {plans.map((plan, i) => {
            const isPopular = i === getPopularIndex();
            const price = getPlanPrice(plan);
            const planCode = plan.code || plan.id;
            return (
              <div key={planCode} className={`rounded-2xl p-6 border ${isPopular ? 'border-[#0a1628] shadow-xl bg-white scale-[1.02]' : 'border-gray-200 bg-white'} relative`} data-testid={`purchase-plan-${planCode}`}>
                {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#0a1628] text-white text-xs font-bold rounded-full">RECOMMENDED</div>}
                <h3 className="text-lg font-bold text-[#0a1628] mb-1">{plan.name}</h3>
                <p className="text-xs text-gray-500 mb-4">{plan.description || ''}</p>
                {(() => {
                  const discountEnabled = !!plan.discount_enabled;
                  const dType = plan.discount_type || 'percent';
                  const dVal = parseFloat((billingCycle === 'yearly' ? plan.discount_value_yearly : plan.discount_value_monthly) ?? plan.discount_value ?? 0) || 0;
                  const dAmt = discountEnabled && dVal > 0 ? (dType === 'percent' ? Math.round(price * (dVal / 100)) : Math.min(dVal, price)) : 0;
                  const afterDiscount = Math.max(0, price - dAmt);
                  const gst = Math.round(afterDiscount * 0.18);
                  const total = afterDiscount + gst;
                  return (
                    <div className="mb-5">
                      {dAmt > 0 && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 rounded-full bg-red-50 border border-red-200 text-red-600 text-[10px] font-semibold">
                          {dType === 'percent' ? `${dVal}% OFF` : `₹${dVal.toLocaleString('en-IN')} OFF`}
                          {plan.discount_label ? ` · ${plan.discount_label}` : ''}
                        </div>
                      )}
                      <div className="flex items-baseline gap-2">
                        {dAmt > 0 && <span className="text-base text-gray-400 line-through">₹{price.toLocaleString('en-IN')}</span>}
                        <span className="text-3xl font-bold text-[#0a1628]">₹{afterDiscount.toLocaleString('en-IN')}</span>
                        <span className="text-xs text-gray-400">/ {billingCycle === 'yearly' ? 'year' : 'month'}</span>
                      </div>
                      <div className="mt-1.5 space-y-0.5 text-xs text-gray-500">
                        {dAmt > 0 && <div className="flex justify-between text-red-600"><span>Discount</span><span>− ₹{dAmt.toLocaleString('en-IN')}</span></div>}
                        <div className="flex justify-between"><span>+ 18% GST</span><span>₹{gst.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between font-semibold text-[#0a1628] pt-1 border-t border-gray-100"><span>Total payable</span><span>₹{total.toLocaleString('en-IN')}</span></div>
                      </div>
                    </div>
                  );
                })()}
                <button
                  onClick={() => handlePurchase(plan)}
                  disabled={loading && selectedPlan === planCode}
                  className={`w-full py-3 rounded-xl font-medium text-sm transition ${isPopular ? 'bg-[#dc2626] text-white hover:bg-[#b91c1c]' : 'border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white'} disabled:opacity-50`}
                  data-testid={`purchase-btn-${planCode}`}
                >
                  {loading && selectedPlan === planCode ? 'Processing…' : 'Purchase Now'}
                </button>
                <ul className="mt-5 space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-600"><Check size={14} weight="bold" className="text-green-500 flex-shrink-0" /> {plan.max_students || 200} Students</li>
                  <li className="flex items-center gap-2 text-sm text-gray-600"><Check size={14} weight="bold" className="text-green-500 flex-shrink-0" /> {(plan.max_users === -1 ? 'Unlimited' : (plan.max_users || 5))} Users</li>
                  {(plan.features || []).map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check size={14} weight="bold" className="text-green-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-400 text-sm mb-4">Need a custom plan? <a href="https://wa.me/919830122122" className="text-[#0a1628] font-medium underline">Contact Sales</a></p>
          <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-green-500" /> Secure Payment via Razorpay</span>
            <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> Free Onboarding</span>
            <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> Cancel Anytime</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasePage;
